import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import JobsCatalog from './JobsCatalog';
import './JobsManagement.css';

function JobsManagement({ subpage = 'management' }) {
  const { apiFetch, hasPermission } = useAuth();
  const { language } = useI18n();
  const canViewJobs = hasPermission('jobs.view');
  const canEditJobs = hasPermission('jobs.edit');
  const [activeSection, setActiveSection] = useState(subpage === 'catalog' ? 'catalog' : 'playerJobs');

  const t = language === 'da'
    ? {
        failedCatalog: 'Kunne ikke hente jobkatalog',
        identifierRequired: 'Spillerens identifikator er påkrævet.',
        failedPlayerJobs: 'Kunne ikke hente spillerjobs',
        failedAdd: 'Kunne ikke tilføje multijob',
        failedRemove: 'Kunne ikke fjerne multijob',
        failedPrimary: 'Kunne ikke sætte primært job',
        addedUpdated: '{job} tilføjet/opdateret for {id}.',
        removed: '{job} fjernet fra multijobs.',
        liveUpdated: 'primært job opdateret live for online spiller via void-link ({job} {grade}).',
        dbUpdated: 'primært job opdateret i database (spiller offline): {job} {grade}.',
        noPermission: 'Du har ikke tilladelse til at se jobs.',
        jobs: 'Jobs',
        playerJobs: 'Spillerjobs',
        jobsCatalog: 'Jobs katalog',
        subtitle: 'Se, tilføj, fjern og sæt primære jobs. primære opdateringer er live for online spillere.',
        placeholder: 'Spiller identifikator (char1:license...)',
        loading: 'Indlæser...',
        loadJobs: 'Indlæs jobs',
        player: 'Spiller',
        name: 'Navn',
        identifier: 'Identifikator',
        primaryJob: 'primært job',
        grade: 'Grade',
        multijobs: 'Multijobs',
        setPrimary: 'sæt primær',
        remove: 'Fjern',
        noMultijobs: 'Ingen multijobs konfigureret.',
        addUpdate: 'tilføj / Opdater multijob',
        loadingCatalog: 'Indlæser jobkatalog...',
        job: 'Job',
        addUpdateBtn: 'tilføj / Opdater multijob',
        setAsPrimary: 'sæt som primær',
        readOnly: 'Kun læsning. Du skal have jobs.edit for at ændre jobs.'
      }
    : {
        failedCatalog: 'Failed to fetch jobs catalog',
        identifierRequired: 'Player identifier is required.',
        failedPlayerJobs: 'Failed to fetch player jobs',
        failedAdd: 'Failed to add multijob',
        failedRemove: 'Failed to remove multijob',
        failedPrimary: 'Failed to set primary job',
        addedUpdated: '{job} added/updated for {id}.',
        removed: '{job} removed from multijobs.',
        liveUpdated: 'Primary job updated live for online player via void-link ({job} {grade}).',
        dbUpdated: 'Primary job updated in database (player offline): {job} {grade}.',
        noPermission: 'You do not have permission to view jobs.',
        jobs: 'Jobs',
        playerJobs: 'Player Jobs',
        jobsCatalog: 'Jobs Catalog',
        subtitle: 'View, add, remove and set primary jobs. Primary updates are live for online players.',
        placeholder: 'Player identifier (char1:license...)',
        loading: 'Loading...',
        loadJobs: 'Load Jobs',
        player: 'Player',
        name: 'Name',
        identifier: 'Identifier',
        primaryJob: 'Primary Job',
        grade: 'Grade',
        multijobs: 'Multijobs',
        setPrimary: 'Set Primary',
        remove: 'Remove',
        noMultijobs: 'No multijobs configured.',
        addUpdate: 'Add / Update Multijob',
        loadingCatalog: 'Loading jobs catalog...',
        job: 'Job',
        addUpdateBtn: 'Add / Update Multijob',
        setAsPrimary: 'Set as Primary',
        readOnly: 'Read-only mode. You need jobs.edit to modify jobs.'
      };

  const text = (template, params = {}) => template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);
  const failedCatalogMessage = t.failedCatalog;

  const [identifier, setIdentifier] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [player, setPlayer] = useState(null);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('0');
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingPlayer, setLoadingPlayer] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Update activeSection when subpage prop changes
  useEffect(() => {
    setActiveSection(subpage === 'catalog' ? 'catalog' : 'playerJobs');
  }, [subpage]);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setLoadingCatalog(true);
        const response = await apiFetch('/api/jobs/catalog');
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || failedCatalogMessage);
        }

        // Handle the response format from fetchJobsCatalogForSite
        const jobs = data.jobs || [];
        setCatalog(jobs);
        
        if (jobs?.length) {
          setSelectedJob(jobs[0].name);
          setSelectedGrade(String(jobs[0]?.grades?.[0]?.grade ?? 0));
        }
      } catch (err) {
        setError(err.message);
        setCatalog([]);
      } finally {
        setLoadingCatalog(false);
      }
    };

    if (canViewJobs) {
      fetchCatalog();
    } else {
      setLoadingCatalog(false);
    }
  }, [apiFetch, canViewJobs, failedCatalogMessage]);

  const selectedJobGrades = useMemo(() => {
    const job = catalog.find((entry) => entry.name === selectedJob);
    return job?.grades || [];
  }, [catalog, selectedJob]);

  const loadPlayerJobs = async () => {
    if (!identifier.trim()) {
      setError(t.identifierRequired);
      return;
    }

    try {
      setLoadingPlayer(true);
      setError('');
      setNotice('');

      const response = await apiFetch(`/api/jobs/player/${encodeURIComponent(identifier.trim())}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedPlayerJobs);
      }

      setPlayer(data.player);
    } catch (err) {
      setPlayer(null);
      setError(err.message);
    } finally {
      setLoadingPlayer(false);
    }
  };

  const addMultiJob = async () => {
    if (!player || !selectedJob) return;

    try {
      setActionLoading(true);
      setError('');
      setNotice('');

      const response = await apiFetch(`/api/jobs/player/${encodeURIComponent(player.identifier)}/multijob`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job: selectedJob,
          grade: Number(selectedGrade)
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedAdd);
      }

      setPlayer((prev) => ({
        ...prev,
        multijobs: data.multijobs || []
      }));
      setNotice(text(t.addedUpdated, { job: selectedJob, id: player.identifier }));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const removeMultiJob = async (jobName) => {
    if (!player) return;

    try {
      setActionLoading(true);
      setError('');
      setNotice('');

      const response = await apiFetch(
        `/api/jobs/player/${encodeURIComponent(player.identifier)}/multijob/${encodeURIComponent(jobName)}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedRemove);
      }

      setPlayer((prev) => ({
        ...prev,
        multijobs: data.multijobs || []
      }));
      setNotice(text(t.removed, { job: jobName }));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const setPrimaryJob = async (jobName, grade) => {
    if (!player) return;

    try {
      setActionLoading(true);
      setError('');
      setNotice('');

      const response = await apiFetch(
        `/api/jobs/player/${encodeURIComponent(player.identifier)}/set-primary`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            job: jobName,
            grade: Number(grade)
          })
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t.failedPrimary);
      }

      setPlayer((prev) => ({
        ...prev,
        currentJob: {
          job: jobName,
          grade: Number(grade)
        }
      }));

      if (data.online) {
        setNotice(text(t.liveUpdated, { job: jobName, grade }));
      } else {
        setNotice(text(t.dbUpdated, { job: jobName, grade }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (!canViewJobs) {
    return (
      <div className="jobs-page">
        <div className="jobs-empty">{t.noPermission}</div>
      </div>
    );
  }

  return (
    <div className="jobs-page">
      <div className="jobs-header">
        <h2>{t.jobs}</h2>
        <p>{t.subtitle}</p>
      </div>

      {activeSection === 'playerJobs' && (
        <PlayerJobsSection {...{ t, text, apiFetch, canViewJobs, canEditJobs, identifier, setIdentifier, loadingPlayer, loadPlayerJobs, player, error, notice, setError, setNotice, setActionLoading, actionLoading, loadingCatalog, catalog, selectedJob, setSelectedJob, selectedJobGrades, selectedGrade, setSelectedGrade, addMultiJob, removeMultiJob, setPrimaryJob }} />
      )}

      {activeSection === 'catalog' && canEditJobs && <JobsCatalog />}
    </div>
  );
}

function PlayerJobsSection({ t, text, apiFetch, canViewJobs, canEditJobs, identifier, setIdentifier, loadingPlayer, loadPlayerJobs, player, error, notice, setError, setNotice, setActionLoading, actionLoading, loadingCatalog, catalog, selectedJob, setSelectedJob, selectedJobGrades, selectedGrade, setSelectedGrade, addMultiJob, removeMultiJob, setPrimaryJob }) {
  return (
    <>
      <div className="jobs-search">
        <input
          type="text"
          placeholder={t.placeholder}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
        <button onClick={loadPlayerJobs} disabled={loadingPlayer || !identifier.trim()}>
          {loadingPlayer ? t.loading : t.loadJobs}
        </button>
      </div>

      {error && <div className="jobs-alert error">{error}</div>}
      {notice && <div className="jobs-alert success">{notice}</div>}

      {player && (
        <div className="jobs-content">
          <div className="jobs-card">
            <h3>{t.player}</h3>
            <div className="jobs-row"><span>{t.name}</span><strong>{player.name}</strong></div>
            <div className="jobs-row">
              <span>{t.identifier}</span>
              <strong className="jobs-mono" title={player.identifier}>{shortIdentifier(player.identifier)}</strong>
            </div>
            <div className="jobs-row"><span>{t.primaryJob}</span><strong>{player.currentJob?.job} ({t.grade} {player.currentJob?.grade})</strong></div>
          </div>

          <div className="jobs-card">
            <h3>{t.multijobs}</h3>
            {player.multijobs?.length ? (
              <div className="jobs-list">
                {player.multijobs.map((entry) => (
                  <div className="jobs-item" key={`${entry.job}-${entry.grade}`}>
                    <div>
                      <strong>{entry.job}</strong>
                      <span>{t.grade} {entry.grade}</span>
                    </div>
                    <div className="jobs-actions">
                      <button
                        onClick={() => setPrimaryJob(entry.job, entry.grade)}
                        disabled={!canEditJobs || actionLoading}
                      >
                        {t.setPrimary}
                      </button>
                      <button
                        className="danger"
                        onClick={() => removeMultiJob(entry.job)}
                        disabled={!canEditJobs || actionLoading}
                      >
                        {t.remove}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="jobs-empty">{t.noMultijobs}</div>
            )}
          </div>

          <div className="jobs-card">
            <h3>{t.addUpdate}</h3>
            {loadingCatalog ? (
              <div className="jobs-empty">{t.loadingCatalog}</div>
            ) : (
              <>
                <div className="jobs-form-row">
                  <label>{t.job}</label>
                  <select
                    value={selectedJob}
                    onChange={(e) => {
                      const nextJob = e.target.value;
                      setSelectedJob(nextJob);
                      const job = catalog.find((entry) => entry.name === nextJob);
                      setSelectedGrade(String(job?.grades?.[0]?.grade ?? 0));
                    }}
                    disabled={!canEditJobs || actionLoading}
                  >
                    {catalog.map((job) => (
                      <option key={job.name} value={job.name}>{job.label || job.name}</option>
                    ))}
                  </select>
                </div>

                <div className="jobs-form-row">
                  <label>{t.grade}</label>
                  <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    disabled={!canEditJobs || actionLoading}
                  >
                    {selectedJobGrades.map((grade) => (
                      <option key={`${selectedJob}-${grade.grade}`} value={grade.grade}>
                        {grade.grade} - {grade.label || `Grade ${grade.grade}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="jobs-actions">
                  <button onClick={addMultiJob} disabled={!canEditJobs || actionLoading || !selectedJob}>
                    {t.addUpdateBtn}
                  </button>
                  <button
                    onClick={() => setPrimaryJob(selectedJob, Number(selectedGrade))}
                    disabled={!canEditJobs || actionLoading || !selectedJob}
                  >
                    {t.setAsPrimary}
                  </button>
                </div>

                {!canEditJobs && (
                  <div className="jobs-empty">{t.readOnly}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function shortIdentifier(value) {
  if (!value || value.length <= 30) {
    return value;
  }

  return `${value.slice(0, 18)}...${value.slice(-8)}`;
}

export default JobsManagement;




